import { FiStar } from 'react-icons/fi';

export const lessCommonVocabData = {
  courseId: 'less-common-vocab',
  courseType: 'vocabulary',
  title: 'Less Common Vocabulary',
  icon: FiStar,
  overview: {
    impact: 'Expand your lexical range to Band 7+ with impressive but natural vocabulary',
    focus: 'Twelve versatile words that work across IELTS Writing and Speaking',
    uses: 'Writing Task 2, Speaking Parts 2–3, Reading recognition',
    duration: '20-25 minutes',
  },
  sections: [
    {
      title: 'What Examiners Look For',
      content: `<p>The IELTS Band 7 descriptor for Lexical Resource states: <em>"uses less common lexical items with some awareness of style and collocation."</em> At Band 8, candidates use them <em>"skilfully"</em> with <em>"rare errors."</em></p>
<p>This does not mean using the longest or rarest word you can find. It means choosing words that are <strong>a step above everyday vocabulary</strong> and using them correctly. A well-placed "meticulous" impresses far more than a misused "sesquipedalian."</p>`,
      points: [
        'Band 7+ requires "less common lexical items" — words beyond the basic 2,000',
        'Accuracy matters more than rarity: a correct "viable" beats an incorrect "efficacious"',
        'Examiners notice even 3-4 well-used less common words across a full essay',
        'These words should replace common ones naturally, not be forced into sentences',
      ],
    },
    {
      title: 'Key Less-Common Words',
      content: `<p>These twelve words are particularly useful because they are versatile enough for many IELTS topics and common in academic writing.</p>
<table>
  <thead><tr><th>Word</th><th>Part of Speech</th><th>Meaning</th><th>Example</th></tr></thead>
  <tbody>
    <tr><td><strong>meticulous</strong></td><td>adjective</td><td>showing great attention to detail</td><td>The research was meticulous in its methodology.</td></tr>
    <tr><td><strong>ubiquitous</strong></td><td>adjective</td><td>found everywhere; extremely common</td><td>Smartphones have become ubiquitous in modern life.</td></tr>
    <tr><td><strong>pragmatic</strong></td><td>adjective</td><td>dealing with things in a practical way</td><td>A pragmatic approach to education reform is needed.</td></tr>
    <tr><td><strong>articulate</strong></td><td>adj/verb</td><td>express clearly; fluent and clear</td><td>She was able to articulate her views with confidence.</td></tr>
    <tr><td><strong>compelling</strong></td><td>adjective</td><td>very convincing or persuasive</td><td>The evidence for climate action is compelling.</td></tr>
    <tr><td><strong>viable</strong></td><td>adjective</td><td>capable of working successfully; feasible</td><td>Renewable energy is now a viable alternative to fossil fuels.</td></tr>
    <tr><td><strong>profound</strong></td><td>adjective</td><td>very great or intense; deep</td><td>Technology has had a profound impact on communication.</td></tr>
    <tr><td><strong>integral</strong></td><td>adjective</td><td>essential; necessary as a part of a whole</td><td>Teamwork is integral to success in the workplace.</td></tr>
    <tr><td><strong>curtail</strong></td><td>verb</td><td>reduce or restrict</td><td>The new law aims to curtail excessive advertising.</td></tr>
    <tr><td><strong>foster</strong></td><td>verb</td><td>encourage the development of</td><td>Schools should foster creativity alongside academic skills.</td></tr>
    <tr><td><strong>fluctuate</strong></td><td>verb</td><td>rise and fall irregularly</td><td>Oil prices tend to fluctuate in response to geopolitical events.</td></tr>
    <tr><td><strong>mitigate</strong></td><td>verb</td><td>make less severe or serious</td><td>Urban planning can mitigate the effects of natural disasters.</td></tr>
  </tbody>
</table>`,
      points: [
        'Adjectives (meticulous, ubiquitous, pragmatic) upgrade basic descriptions instantly',
        'Verbs (curtail, foster, mitigate) replace vague verbs like "reduce", "help", "make better"',
        '"Articulate" works as both adjective and verb — learn both uses',
        'These twelve words cover topics from technology to education to environment',
      ],
    },
    {
      title: 'Using Them in Speaking',
      content: `<p>Less common vocabulary in Speaking should sound <strong>natural, not rehearsed</strong>. The best approach is to incorporate these words into your Part 2 and Part 3 answers where they fit organically.</p>
<h4>Part 2 Example (Describe a skill)</h4>
<p><em>"I'd say I'm quite meticulous when it comes to organising things — I think it's an integral part of being efficient at work."</em></p>
<h4>Part 3 Example (Abstract discussion)</h4>
<p><em>"I think governments need to take a pragmatic approach to environmental policy rather than making promises that aren't viable in the short term."</em></p>`,
      points: [
        'In Part 2, weave 1-2 less common words into your extended answer',
        'In Part 3, use them when giving opinions or analysing abstract ideas',
        'Do not pause awkwardly before a sophisticated word — it must flow naturally',
        'Practise saying these words aloud until they feel comfortable in your mouth',
      ],
    },
    {
      title: 'Using Them in Writing',
      content: `<p>Writing is where less common vocabulary has the biggest impact on your score. Each body paragraph is an opportunity to demonstrate lexical range.</p>
<h4>Practical Strategy</h4>
<p>When drafting your essay, write naturally first. Then, during review, identify 3-4 common words you can upgrade:</p>
<ul>
  <li>"important" → <strong>integral</strong> or <strong>compelling</strong></li>
  <li>"reduce" → <strong>curtail</strong> or <strong>mitigate</strong></li>
  <li>"help develop" → <strong>foster</strong></li>
  <li>"everywhere" → <strong>ubiquitous</strong></li>
  <li>"practical" → <strong>pragmatic</strong> or <strong>viable</strong></li>
</ul>`,
      points: [
        'Write naturally first, then upgrade 3-4 words during your 2-minute review',
        'Spread less common words across paragraphs — don\'t cluster them',
        'Use them in topic sentences for maximum visibility to the examiner',
        'Pair less common words with clear context so meaning is obvious',
      ],
    },
  ],
  exercises: [
    {
      title: 'Match Word to Definition',
      type: 'select',
      instructions: 'Select the correct definition for each less common word.',
      questions: [
        {
          text: 'What does "meticulous" mean?',
          options: ['very fast', 'showing great attention to detail', 'extremely expensive', 'widely known'],
          answer: 'showing great attention to detail',
          explanation: '"Meticulous" describes someone who is extremely careful and precise in their work.',
        },
        {
          text: 'What does "curtail" mean?',
          options: ['extend significantly', 'reduce or restrict', 'investigate thoroughly', 'celebrate publicly'],
          answer: 'reduce or restrict',
          explanation: '"Curtail" means to reduce in extent or quantity, or to impose a restriction on something.',
        },
        {
          text: 'What does "ubiquitous" mean?',
          options: ['rarely seen', 'found everywhere', 'extremely beautiful', 'highly technical'],
          answer: 'found everywhere',
          explanation: '"Ubiquitous" means present, appearing, or found everywhere — extremely widespread.',
        },
        {
          text: 'What does "foster" mean?',
          options: ['prevent from growing', 'encourage the development of', 'criticise harshly', 'ignore deliberately'],
          answer: 'encourage the development of',
          explanation: '"Foster" means to encourage or promote the development of something.',
        },
      ],
    },
    {
      title: 'Fill in the Correct Word',
      type: 'fill-in',
      instructions: 'Complete each sentence with the most appropriate less common word from the lesson.',
      questions: [
        {
          text: 'The government should _______ a culture of innovation in schools by investing in creative programmes.',
          answer: 'foster',
          explanation: '"Foster" means to encourage the development of — it fits perfectly with "a culture of innovation."',
        },
        {
          text: 'Smartphone use has become _______ among teenagers, with some studies reporting over six hours of daily screen time.',
          answer: 'ubiquitous',
          explanation: '"Ubiquitous" means found everywhere, which describes the widespread adoption of smartphones.',
        },
        {
          text: 'Improved urban planning can help _______ the effects of flooding in coastal cities.',
          answer: 'mitigate',
          explanation: '"Mitigate" means to make less severe — urban planning reduces the severity of flooding impacts.',
        },
        {
          text: 'House prices in the capital tend to _______ depending on economic conditions and interest rates.',
          answer: 'fluctuate',
          explanation: '"Fluctuate" means to rise and fall irregularly, which describes changing house prices.',
        },
      ],
    },
    {
      title: 'Identify Correct Usage',
      type: 'select',
      instructions: 'Which sentence uses the highlighted word correctly?',
      questions: [
        {
          text: 'Which sentence uses "pragmatic" correctly?',
          options: [
            'The sunset was very pragmatic and beautiful.',
            'We need a pragmatic solution that can be implemented within the current budget.',
            'She felt pragmatic after hearing the good news.',
            'The food was pragmatic and delicious.',
          ],
          answer: 'We need a pragmatic solution that can be implemented within the current budget.',
          explanation: '"Pragmatic" means practical and realistic. It describes approaches, solutions, and attitudes — not emotions or aesthetics.',
        },
        {
          text: 'Which sentence uses "compelling" correctly?',
          options: [
            'The weather was compelling today.',
            'She gave a compelling argument for investing in renewable energy.',
            'The room was very compelling and spacious.',
            'He felt compelling after exercising.',
          ],
          answer: 'She gave a compelling argument for investing in renewable energy.',
          explanation: '"Compelling" means very convincing or persuasive. It describes arguments, evidence, reasons, and narratives.',
        },
        {
          text: 'Which sentence uses "integral" correctly?',
          options: [
            'The cake was integral and tasty.',
            'She wore an integral dress to the party.',
            'Critical thinking is integral to academic success.',
            'The weather became integral in the afternoon.',
          ],
          answer: 'Critical thinking is integral to academic success.',
          explanation: '"Integral" means essential or necessary as part of a whole — it describes components that are fundamental.',
        },
        {
          text: 'Which sentence uses "viable" correctly?',
          options: [
            'The concert was viable and entertaining.',
            'Solar power is now a commercially viable energy source.',
            'He felt viable after a good sleep.',
            'The restaurant served viable food.',
          ],
          answer: 'Solar power is now a commercially viable energy source.',
          explanation: '"Viable" means capable of working successfully — feasible. It describes plans, options, alternatives.',
        },
      ],
    },
    {
      title: 'Choose the Speaking Context',
      type: 'select',
      instructions: 'In which Speaking Part context would the given word sound most natural?',
      questions: [
        {
          text: 'Where would "meticulous" sound most natural?',
          options: [
            'Part 1: "I\'m meticulous about what I eat for breakfast."',
            'Part 2: "My father is incredibly meticulous — he plans every family trip down to the smallest detail."',
            'Part 1: "The meticulous weather today is nice."',
          ],
          answer: 'Part 2: "My father is incredibly meticulous — he plans every family trip down to the smallest detail."',
          explanation: 'Part 2 (extended answer) is ideal for descriptive adjectives like "meticulous" when describing a person or experience.',
        },
        {
          text: 'Where would "pragmatic" sound most natural?',
          options: [
            'Part 1: "I have a pragmatic favourite colour."',
            'Part 3: "I think governments need to be more pragmatic when designing education policies."',
            'Part 1: "My pragmatic hobby is reading."',
          ],
          answer: 'Part 3: "I think governments need to be more pragmatic when designing education policies."',
          explanation: 'Part 3 (abstract discussion) is perfect for words like "pragmatic" when analysing societal issues.',
        },
        {
          text: 'Where would "profound" sound most natural?',
          options: [
            'Part 1: "I had a profound breakfast today."',
            'Part 2: "Travelling to Japan had a profound effect on the way I see the world."',
            'Part 1: "My profound friend called me."',
          ],
          answer: 'Part 2: "Travelling to Japan had a profound effect on the way I see the world."',
          explanation: '"Profound" describes deep impact or significance — ideal for Part 2 when reflecting on meaningful experiences.',
        },
      ],
    },
    {
      title: 'Write with Less Common Vocabulary',
      type: 'writing',
      instructions: 'Write a complete IELTS-style sentence using the given word correctly and naturally.',
      questions: [
        {
          text: 'Write a Task 2 sentence using "curtail".',
          sampleAnswer: 'Governments should introduce legislation to curtail the amount of single-use plastic produced by manufacturers.',
          explanation: '"Curtail" is used correctly as a verb meaning to reduce or restrict — applied to a policy context.',
        },
        {
          text: 'Write a Task 2 sentence using "compelling".',
          sampleAnswer: 'There is compelling evidence that early childhood education has long-lasting benefits for both individuals and society as a whole.',
          explanation: '"Compelling" correctly describes evidence that is very convincing and hard to argue against.',
        },
      ],
    },
  ],
  tips: [
    'Focus on 12 versatile words rather than memorising 50 obscure ones',
    'Test yourself by writing sentences — if you cannot use the word correctly in context, review its meaning',
    'Create personal flashcards: word on front, definition + example sentence on back',
    'In Writing, upgrade words during your review: "important" → integral, "reduce" → curtail',
    'In Speaking, practise until these words flow naturally — pause-free delivery matters',
  ],
  dailyPractice: [
    'Pick three words from the table and write an IELTS-style sentence for each',
    'Read a news article and replace 3 common words with less common alternatives',
    'Practise a Speaking Part 2 answer and include at least 2 less common words',
    'Quiz a study partner: give definitions and let them guess the word',
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'Less Common Vocabulary for Band 7+: Twelve versatile words (meticulous, ubiquitous, pragmatic, articulate, compelling, viable, profound, integral, curtail, foster, fluctuate, mitigate) with natural usage in Speaking and Writing. Difficulty 4.',
};

export function getDataForDifficulty(difficulty = 4) {
  if (difficulty <= 2) {
    return {
      ...lessCommonVocabData,
      title: lessCommonVocabData.title + ' (Foundations)',
      overview: { ...lessCommonVocabData.overview, duration: '15-20 minutes' },
      sections: lessCommonVocabData.sections.slice(0, Math.max(2, Math.ceil(lessCommonVocabData.sections.length / 2))),
      exercises: lessCommonVocabData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...lessCommonVocabData,
      title: lessCommonVocabData.title + ' (Advanced)',
      overview: { ...lessCommonVocabData.overview, duration: '30-35 minutes' },
      sections: lessCommonVocabData.sections,
      exercises: lessCommonVocabData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(lessCommonVocabData.tips || []),
        'For Band 8+: Use less common vocabulary naturally in context.',
        'Advanced tip: Focus on collocations and word families, not just individual words.',
      ],
    };
  }
  return lessCommonVocabData;
}
